import { AxiosResponse } from 'axios';
import { useSelector } from 'react-redux';

import { ICreateSubmissionRequest } from 'types/services/baseServiceTypes';

import { createServiceInstance } from '../baseService';
import {
  IGetAssessmentDataRequest,
  IGetAssessmentDataResponse,
  ILmsSubmissionFormData,
  ILmsSubmissionResponse
} from './lmsServiceTypes';

/**
 * LMS service model
 * @description Returns object with lms services
 * @typedef {object} ILmsService
 */
export interface ILmsService {
  getAssessmentData: (request: IGetAssessmentDataRequest) => Promise<AxiosResponse<IGetAssessmentDataResponse>>;
  recreationalSubmission: (request: ICreateSubmissionRequest<ILmsSubmissionFormData>) => Promise<AxiosResponse<ILmsSubmissionResponse>>;
  commercialSubmission: (request: ICreateSubmissionRequest<ILmsSubmissionFormData>) => Promise<AxiosResponse<ILmsSubmissionResponse>>;
}

/**
 * LMS service hook
 * @description Gives access to all services relating to user
 */
const useLmsService = () : ILmsService => {
  const token = useSelector((state: any) => state.B2CSettings.items.accesstokenForms);
  const webappurlFormsApi = useSelector((state: any) => state.B2CSettings.items.webappurlFormsApi);

  const getInstance = () => createServiceInstance(`${webappurlFormsApi}/api/myCASA/Forms`, token);

  /**
   * Get Assessment Data
   * @description Get questions and answers for LMS assessment
   * @param {IGetAssessmentDataRequest} request Get assessment data request
   * @returns {Promise<AxiosResponse<IGetAssessmentDataResponse>>} Promise with IGetAssessmentDataResponse
   */
  const getAssessmentData = (request: IGetAssessmentDataRequest): Promise<AxiosResponse<IGetAssessmentDataResponse>> => {
    const axiosInstance = getInstance();

    return axiosInstance.post<IGetAssessmentDataResponse>('/getAssessmentData/v1', request);
  };

  /**
   * Recreational Submission service
   *
   * @description Submit Recreational LMS assessment
   * @param {ICreateSubmissionRequest<ILmsSubmissionFormData>} request LMS Submission request
   * @returns {Promise<AxiosResponse<ILmsSubmissionResponse>>} Promise with ILmsSubmissionResponse
   */
  const recreationalSubmission = (request: ICreateSubmissionRequest<ILmsSubmissionFormData>): Promise<AxiosResponse<ILmsSubmissionResponse>> => {
    const axiosInstance = getInstance();

    return axiosInstance.post<ILmsSubmissionResponse>('/Submissions/lms/recreational/v1', request);
  };

  /**
   * Commercial Submission service
   *
   * @description Submit Commercial LMS assessment
   * @param {ICreateSubmissionRequest<ILmsSubmissionFormData>} request LMS Submission request
   * @returns {Promise<AxiosResponse<ILmsSubmissionResponse>>} Promise with ILmsSubmissionResponse
   */
  const commercialSubmission = (request: ICreateSubmissionRequest<ILmsSubmissionFormData>): Promise<AxiosResponse<ILmsSubmissionResponse>> => {
    const axiosInstance = getInstance();

    return axiosInstance.post<ILmsSubmissionResponse>('/Submissions/lms/commercial/v1', request);
  };

  return {
    getAssessmentData,
    recreationalSubmission,
    commercialSubmission
  };
};

export default useLmsService;